/**
 * Zone de drop fichier reutilisable (drag&drop OR file picker).
 * Validation client : taille max 25MB + MIME whitelist (PDF/JPG/PNG).
 *
 * @param {{
 *   onFile: (file: File) => void,
 *   accept?: string,
 *   compact?: boolean,
 *   disabled?: boolean,
 * }} props
 */

import { useState, useRef } from 'react';
import { validateUpload, MAX_BYTES, ALLOWED_MIME } from '../lib/validateUpload.js';

export default function UploadDropzone({ onFile, accept = '.pdf,.jpg,.jpeg,.png', compact = false, disabled = false }) {
  const [hover, setHover] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const handleFile = (file) => {
    setError('');
    if (!file) return;
    const err = validateUpload(file);
    if (err) {
      setError(err);
      return;
    }
    onFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setHover(false);
    if (disabled) return;
    handleFile(e.dataTransfer.files?.[0]);
  };

  const onPick = (e) => handleFile(e.target.files?.[0]);

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); if (!disabled) setHover(true); }}
        onDragLeave={() => setHover(false)}
        onDrop={onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        className={`border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
          compact ? 'py-3 px-3 text-xs' : 'py-6 px-4 text-sm'
        } ${
          disabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed text-gray-400'
            : hover
              ? 'border-blue-400 bg-blue-50 text-blue-700'
              : 'border-gray-300 bg-white text-[#64748b] hover:border-gray-400'
        }`}
        role="button"
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={onPick}
          className="hidden"
          disabled={disabled}
        />
        {compact ? 'Cliquer ou deposer un fichier' : 'Glisser un fichier ici ou cliquer pour choisir'}
        <div className={compact ? 'text-[10px] mt-0.5' : 'text-xs mt-1'}>PDF / JPG / PNG · max 25 MB</div>
      </div>
      {error && <div className="text-xs text-red-600 mt-1">{error}</div>}
    </div>
  );
}

export { MAX_BYTES, ALLOWED_MIME };
